import { PaletteShades } from "../common/types";

// ---------------------------------------------------------------------------
// Hex ↔ HSL conversions
// ---------------------------------------------------------------------------

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace("#", "");
  const full = clean.length === 3
    ? clean.split("").map((c) => c + c).join("")
    : clean;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return [h * 360, s, l];
}

function hslToHex(h: number, s: number, l: number): string {
  const hn = h / 360;
  const hue2rgb = (p: number, q: number, t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  if (s === 0) {
    const v = Math.round(l * 255);
    return `#${v.toString(16).padStart(2, "0").repeat(3)}`;
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = Math.round(hue2rgb(p, q, hn + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, hn) * 255);
  const b = Math.round(hue2rgb(p, q, hn - 1 / 3) * 255);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// Shade generation
//
// Strategy: treat the input hex as the "500" (mid-range) brand colour.
// Lighter shades (50–400) increase lightness toward white while keeping hue/sat.
// Darker shades (600–900) decrease lightness toward near-black.
// Saturation is gently modulated so light shades don't look washed-out.
// ---------------------------------------------------------------------------

export function generatePaletteFromHex(brandHex: string): PaletteShades {
  const [r, g, b] = hexToRgb(brandHex);
  const [h, s, l] = rgbToHsl(r, g, b);

  // Clamp saturation for lighter shades so they don't look too grey
  const sLight = Math.min(s, 0.95);

  return {
    50:  hslToHex(h, Math.min(sLight, 0.6), 0.97),
    100: hslToHex(h, Math.min(sLight, 0.7), 0.93),
    200: hslToHex(h, Math.min(sLight, 0.8), 0.86),
    300: hslToHex(h, Math.min(sLight, 0.9), 0.76),
    400: hslToHex(h, sLight,               0.66),
    500: brandHex,
    600: hslToHex(h, s, Math.max(l * 0.83, 0.05)),
    700: hslToHex(h, s, Math.max(l * 0.67, 0.04)),
    800: hslToHex(h, s, Math.max(l * 0.50, 0.03)),
    900: hslToHex(h, s, Math.max(l * 0.35, 0.02)),
  };
}

// ---------------------------------------------------------------------------
// WCAG 2.1 contrast utilities
// ---------------------------------------------------------------------------

function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((c) => {
    const srgb = c / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(hex1: string, hex2: string): number {
  const l1 = relativeLuminance(hex1);
  const l2 = relativeLuminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/** WCAG AA: 4.5:1 for normal text, 3:1 for large text */
export function meetsContrastAA(fg: string, bg: string, largeText = false): boolean {
  return contrastRatio(fg, bg) >= (largeText ? 3 : 4.5);
}

/** Returns whether a hex string is syntactically valid. */
export function isValidHex(hex: string): boolean {
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex.trim());
}

/** Suggest a readable foreground colour (black or white) for a given background. */
export function readableForeground(bgHex: string): "#ffffff" | "#000000" {
  return relativeLuminance(bgHex) > 0.179 ? "#000000" : "#ffffff";
}
